import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { Button } from "../Button/Button";
import { TextInput } from "../TextInput/TextInput";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CopyControl.module.less";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { useCopyToClipboard } from "client/Hooks";

export type CopyControlHandle = {
	invoke: () => void;
};

export type CopyControlProps = {
	value?: string;
	trackingId?: string;
	buttonKind: "primary" | "secondary";
};

export const CopyControl = forwardRef(
	(props: CopyControlProps, ref: Ref<CopyControlHandle>): ReactElement => {
		const {
			value = "",
			trackingId,
			buttonKind
		} = props;

		const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

		useCopyToClipboard(
			buttonRef,
			value
		);

		useImperativeHandle(
			ref,
			(): CopyControlHandle => ({
				invoke: () => buttonRef.current
					?.click()
			}),
			[]
		);

		const isCopyable: boolean = Boolean(value);

		return (
			<div className={styles.root} {...setTrackingId(trackingId)}>
				<div className={styles.valueContainer}>
					<TextInput
						isDisabled
						value={value}
						attachedOn="right"
						trackingId={generateTrackingId(trackingId, "__input")}
					/>
				</div>

				<Button
					kind={buttonKind}
					text={localize("_Copy")}
					ref={buttonRef}
					isDisabled={!isCopyable}
					attachedOn="left"
					trackingId={generateTrackingId(trackingId, "__copy")}
				/>
			</div>
		);
	}
);

CopyControl.displayName = "CopyControl";
